import {
  CHALLENGE_LABEL,
  CHALLENGES_URL,
  moreGameLabel,
  nextDayLabel,
  nextDayThisWeek,
  type GameButtonColor,
  type GameType,
  type SimpleDate,
} from '@puzzlio/core';
import { GAME_META, PLAYABLE_GAMES } from './catalog';

/*
  Picks where the solved screen's one forward-looking button sends the player.

  Rules are evaluated top to bottom and the first match wins, so the ladder is ordered by how close the
  destination is to what they were just doing:

    1. Another day of this game, this week. Finishing Wednesday's puzzle on Friday leaves Thursday and
       Friday sitting there; the nearest thing to "more of this" is the rest of the week.
    2. Another game's puzzle for today. Only reached after finishing TODAY's puzzle, where rule 1 has
       nothing to offer by definition — there is no later day yet. Games they already play come first,
       and only the ones they haven't finished today; the point is to fill in the rest of today rather
       than send them somewhere they've been. Once those are done an unplayed game takes the slot,
       pointed at its tutorial rather than dropping someone cold into a daily. Early-access games are
       in play for subscribers only — see `others` below.
    2½. This week's challenge. A subscriber who has just finished the last of today's puzzles is offered
       a weekly challenge they haven't finished: this game's if it has one, else another game's. It sits
       below rule 2 deliberately, "Try X" included: a challenge is the hardest puzzle published, and a
       player new to a game belongs in its tutorial, not its challenge. And above rule 3, which fires at
       the same moment, so would otherwise never let it through.
    3. That day's results. Every game is finished for the date just played, so the button stops
       proposing more puzzles and offers the day itself — today's or an archive day's alike. Whether
       there is such a page, and whether this player may see it, is the caller's to say: `resultsHref`
       is absent when there isn't one.
    4. The archive. Always available, so the button is never absent.

  A weekly challenge's own solved screen has a ladder of its own (see afterChallenge): another of this
  week's challenges, then whatever of today is left, then the challenges page.

  Every input a rule needs is passed in, because the two clients hold the same facts in different
  shapes and reach different conclusions from them. The app has no results page and no intro mode, so
  it simply doesn't offer rules 2b and 3. That isn't a rule about *where to send a player*, which is
  why it isn't in here.

  Rule 2's pick within a tier is random rather than ordered, so no single game always inherits the
  moment. `seed` belongs to the caller and must be stable for the life of the screen — recomputing it
  per render would reshuffle the label under the player's cursor.
*/

/** Coarse per-date state, as both clients' play-status APIs report it. */
export type DayPlayStatus = 'complete' | 'incomplete' | 'not-started';

/**
 * One game's state on one date. `undefined` means there is no board — a game that launched later, or
 * a day that game skipped — which is a different answer from "not started" and is treated as such.
 */
export type PlayStatusLookup = (game: GameType, date: SimpleDate) => DayPlayStatus | undefined;

export type NextPuzzle = {
  /** A route both clients serve: `GameMeta`'s own URLs, or the `resultsHref` the caller supplied. */
  href: string;
  label: string;
  /** The destination game's button color — rule 2 sends them elsewhere, and the color should say so. */
  color: GameButtonColor;
};

export type NextPuzzleInput = {
  currentGameType: GameType;
  /** Date of the board just completed. Undefined for tutorial / practice boards, which have no date. */
  boardDate: SimpleDate | undefined;
  /** Today in the player's timezone. */
  today: SimpleDate;
  /** Whether `boardDate` falls in the current week — rules 1 and 2 only make sense if it does. */
  isCurrentWeekPuzzle: boolean;
  /** Every game's state over the week *containing boardDate* — this week for a daily, an old week for
   *  an archive puzzle. Undefined while that is still unknown, which drops to the archive. */
  statusOn: PlayStatusLookup | undefined;
  /** Games the player has yet to graduate from onboarding, which rule 2 sends to the tutorial instead.
   *  A client with no tutorial to send them to passes none. */
  introMode: Partial<Record<GameType, boolean>>;
  /** Whether the player's subscription is active — gates the early-access games. */
  isSubscribed: boolean;
  /** Games this client can open at all. Defaults to every playable game; the app passes the narrower
   *  set it has boards for, so the button can't offer a puzzle its own game screen would refuse. */
  openable?: GameType[];
  /** Where this day's results live, or undefined when the client has no such page or the player may
   *  not see it — a button that bounces you to a login screen is worse than the archive link it
   *  would have replaced. */
  resultsHref: string | undefined;
  /** A number in [0, 1), held stable by the caller. */
  seed: number;
  /** This week's weekly challenges, with the player's progress on each. Rule 2½ offers them to
   *  subscribers only; a client that can't open challenges passes none. */
  challenges?: ChallengeStatus[];
  /** The Monday of the challenge just completed, on a challenge's solved screen; `boardDate` is then
   *  undefined, and `statusOn` should cover the current week, for afterChallenge's "rest of today". */
  challengeWeek?: SimpleDate;
};

/** A weekly challenge and how far the player has got with it. */
export type ChallengeStatus = { gameType: GameType; week: SimpleDate; status: DayPlayStatus };

/** A challenge as the button offers it, in its game's color. */
function playChallenge({ gameType, week }: ChallengeStatus): NextPuzzle {
  const game = GAME_META[gameType];
  return { href: game.challengeURL(week), label: `Play the ${game.label} Challenge`, color: game.buttonColor };
}

/**
 * Rule 2's pick among `games` for today: one they play but haven't finished today, else a game they've
 * never played, by its tutorial. Undefined when neither tier has anything.
 */
function todaysPuzzle(
  games: GameType[],
  {
    today,
    statusOn,
    introMode,
    seed,
  }: Pick<NextPuzzleInput, 'today' | 'introMode' | 'seed'> & {
    statusOn: PlayStatusLookup;
  }
): NextPuzzle | undefined {
  const unfinishedToday = games.filter((gameType) => !introMode[gameType] && statusOn(gameType, today) !== 'complete');
  const neverPlayed = games.filter((gameType) => introMode[gameType]);

  const tier = unfinishedToday.length > 0 ? unfinishedToday : neverPlayed;
  if (tier.length === 0) return undefined;
  const gameType = tier[Math.floor(seed * tier.length)];
  const other = GAME_META[gameType];
  return introMode[gameType]
    ? { href: other.tutorialURL, label: `Try ${other.label}`, color: other.buttonColor }
    : { href: other.gameURL(today), label: `Play Today's ${other.label}`, color: other.buttonColor };
}

/** Games this player can be offered today: openable, and early-access ones only to subscribers. */
function offerableGames({ openable, isSubscribed }: Pick<NextPuzzleInput, 'openable' | 'isSubscribed'>) {
  return (openable ?? PLAYABLE_GAMES.map((game) => game.type)).filter(
    (gameType) => isSubscribed || GAME_META[gameType].availability !== 'earlyAccess'
  );
}

/**
 * The ladder on a weekly challenge's solved screen: another of this week's challenges the player hasn't
 * finished, else whatever of today is left (rule 2's tiers, this game's daily included), else the page
 * of challenges.
 */
function afterChallenge(input: NextPuzzleInput): NextPuzzle {
  const { currentGameType, challengeWeek, challenges = [], statusOn, seed } = input;
  const unfinished = challenges.filter(
    (challenge) =>
      challenge.status !== 'complete' && !(challenge.gameType === currentGameType && challenge.week === challengeWeek)
  );
  if (unfinished.length > 0) return playChallenge(unfinished[Math.floor(seed * unfinished.length)]);

  const today = statusOn && todaysPuzzle(offerableGames(input), { ...input, statusOn });
  if (today) return today;

  return { href: CHALLENGES_URL, label: 'More Challenges', color: GAME_META[currentGameType].buttonColor };
}

/** Rule 4: the plain archive link. */
function moreOfThisGame(currentGameType: GameType, boardDate: SimpleDate | undefined): NextPuzzle {
  const config = GAME_META[currentGameType];
  return { href: config.archiveURL(boardDate), label: moreGameLabel(config.label), color: config.buttonColor };
}

export function selectNextPuzzle(input: NextPuzzleInput): NextPuzzle {
  const { currentGameType, boardDate, today, isCurrentWeekPuzzle, statusOn, isSubscribed, resultsHref, seed } = input;
  if (input.challengeWeek) return afterChallenge(input);

  const config = GAME_META[currentGameType];
  const archive = moreOfThisGame(currentGameType, boardDate);

  if (!boardDate || !statusOn) return archive;

  // Early-access games are subscriber-only, so for anyone else they are not part of the day at all.
  // Dropping them here covers both places `others` is used: rule 2 would otherwise offer a puzzle that
  // opens onto a paywall, and rule 3 would count a game they cannot finish against the day, putting the
  // results page permanently out of reach.
  const others = offerableGames(input).filter((gameType) => gameType !== currentGameType);

  // Rules 1 and 2 are about the here and now, and only make sense inside the current week: every day of
  // this week is "later than" a board from two months ago, and there is no "rest of today" to fill in
  // when the puzzle just finished is from March.
  if (isCurrentWeekPuzzle) {
    // ─── 1. A later day of this game, this week ──
    const nextDay = nextDayThisWeek(boardDate, today, (date) => {
      const status = statusOn(currentGameType, date);
      return !!status && status !== 'complete';
    });
    if (nextDay) {
      return { href: config.gameURL(nextDay), label: nextDayLabel(nextDay), color: config.buttonColor };
    }

    if (boardDate === today) {
      // ─── 2. Another game's puzzle for today ──
      const otherToday = todaysPuzzle(others, { ...input, statusOn });
      if (otherToday) return otherToday;

      // ─── 2½. This week's challenge ──
      // Rule 2 found nothing, so every other game is finished today (it counts a game with no status as
      // unfinished), and this one is finished by definition: the player is done for the day.
      const unfinished = isSubscribed ? (input.challenges ?? []).filter((c) => c.status !== 'complete') : [];
      if (unfinished.length > 0) {
        const own = unfinished.find((challenge) => challenge.gameType === currentGameType);
        return own
          ? { ...playChallenge(own), label: `Play the ${CHALLENGE_LABEL}` }
          : playChallenge(unfinished[Math.floor(seed * unfinished.length)]);
      }
    }
  }

  // ─── 3. That day's results ──
  // The current game is complete by definition — this runs on its solved screen — so the question is
  // only whether the others are. Games with no board that day (one that launched later, say) are not
  // counted against the day; a date where nothing else ran isn't a day worth a results page.
  const otherStatuses = others.map((gameType) => statusOn(gameType, boardDate)).filter((status) => !!status);
  if (resultsHref && otherStatuses.length > 0 && otherStatuses.every((status) => status === 'complete')) {
    return { href: resultsHref, label: 'Daily Results', color: config.buttonColor };
  }

  // ─── 4. The archive ──
  return archive;
}
